<template>
  <div class="date-time-range-picker d-flex" style="gap: 10px">
    <date-picker
      v-model="date"
      :readonly="readonly"
      :min="minDate"
      :max="maxDate"
      :variant="variant"
      :date-disabled-fn="dateDisabledFn"
      :date-format-options="{
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      }"
      locale="en-US"
      style="width: 178px"
      calendar-width="230px"
      hide-header
    />
    <b-dropdown
      :disabled="disabled"
      :variant="variant"
      :no-flip="true"
      :popper-opts="{ positionFixed: true }"
      class="border rounded"
      style="width: 124px"
      no-caret
    >
      <template slot="button-content">
        <div class="d-flex align-items-center">
          <div style="font-size: 13px; margin-right: 6px;">
            <i
              class="fa fa-clock-o color-custom-black"
              style="font-size: 20px;"
              aria-hidden="true"
            ></i>
          </div>
          <span :style="sTimeLabel ? '' : 'color: #909db8;'">{{
            sTimeLabel ? sTimeLabel : "HH:mm"
          }}</span>
        </div>
      </template>
      <b-dropdown-form class="px-2">
        <div class="d-flex flex-column justify-content-center">
          <div
            v-if="showAllDay"
            class="d-flex justify-content-start align-items-center mb-2"
          >
            <vue-toggles
              @click="allDay = !allDay"
              :value="allDay"
              height="30"
              width="50"
              checkedText=""
              uncheckedText=""
              checkedBg="#34C759"
              uncheckedBg="#FF6161"
            />
            <span class="ml-2">All Day</span>
          </div>
          <b-time
            v-model="sTime"
            :readonly="readonly || allDay"
            locale="en"
            hide-header
          />
        </div>
      </b-dropdown-form>
    </b-dropdown>
    <b-dropdown
      :disabled="disabled"
      :variant="variant"
      :no-flip="true"
      :popper-opts="{ positionFixed: true }"
      class="border rounded"
      style="width: 124px"
      no-caret
    >
      <template slot="button-content">
        <div class="d-flex align-items-center">
          <div style="font-size: 13px; margin-right: 6px;">
            <i
              class="fa fa-clock-o color-custom-black"
              style="font-size: 20px;"
              aria-hidden="true"
            ></i>
          </div>
          <span :style="eTimeLabel ? '' : 'color: #909db8;'">{{
            eTimeLabel ? eTimeLabel : "HH:mm"
          }}</span>
        </div>
      </template>
      <b-dropdown-form class="px-2">
        <div class="d-flex flex-column justify-content-center">
          <div
            v-if="showAllDay"
            class="d-flex justify-content-start align-items-center mb-2"
          >
            <vue-toggles
              @click="allDay = !allDay"
              :value="allDay"
              height="30"
              width="50"
              checkedText=""
              uncheckedText=""
              checkedBg="#34C759"
              uncheckedBg="#FF6161"
            />
            <span class="ml-2">All Day</span>
          </div>
          <b-time
            v-model="eTime"
            :readonly="readonly || allDay"
            locale="en"
            hide-header
          />
        </div>
      </b-dropdown-form>
    </b-dropdown>
  </div>
</template>

<script>
import moment from "moment";
import DatePicker from "./DatePicker.vue";

export default {
  name: "DateTimeRangePicker",
  components: { DatePicker },
  props: {
    value: {
      type: Object
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array,
      default: () => []
    },
    minDate: {
      type: [Date, String],
      default: null
    },
    maxDate: {
      type: [Date, String],
      default: null
    },
    dateDisabledFn: {
      type: Function,
      default: null
    },
    showAllDay: {
      type: Boolean,
      default: true
    },
    beginTime: {
      type: String,
      default: "00:00:00"
    },
    endTime: {
      type: String,
      default: "23:59:59"
    }
  },
  computed: {
    date: {
      get() {
        return this.value?.date;
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          date: newValue
        });
      }
    },
    sTime: {
      get() {
        return this.value?.sTime;
      },
      set(newValue) {
        const emit = value => {
          this.$emit("input", {
            ...this.value,
            sTime: value
          });
        };

        if (!this.beginTime || !this.endTime) {
          emit(newValue);
          return;
        }

        const sTime = moment(newValue, "HH:mm:ss");

        const beginTime = moment(this.beginTime, "HH:mm:ss");
        const endTime = moment(this.endTime, "HH:mm:ss");

        if (sTime.isBefore(beginTime)) {
          const diff = beginTime.diff(sTime);

          if (diff < 3600000 && endTime.minutes() === 0) {
            endTime.subtract(diff);
          }

          emit(endTime.format("HH:mm:ss"));
        } else if (sTime.isAfter(endTime)) {
          const diff = endTime.diff(sTime);

          if (diff > -3600000 && beginTime.minutes() === 0) {
            beginTime.subtract(diff);
          }

          emit(beginTime.format("HH:mm:ss"));
        } else {
          emit(sTime.format("HH:mm:ss"));
        }
      }
    },
    eTime: {
      get() {
        return this.value?.eTime;
      },
      set(newValue) {
        const emit = value => {
          this.$emit("input", {
            ...this.value,
            eTime: value
          });
        };

        if (!this.beginTime || !this.endTime) {
          emit(newValue);
          return;
        }

        const eTime = moment(newValue, "HH:mm:ss");

        const beginTime = moment(this.beginTime, "HH:mm:ss");
        const endTime = moment(this.endTime, "HH:mm:ss");

        if (eTime.isBefore(beginTime)) {
          const diff = beginTime.diff(eTime);

          if (diff < 3600000 && endTime.minutes() === 0) {
            endTime.subtract(diff);
          }

          emit(endTime.format("HH:mm:ss"));
        } else if (eTime.isAfter(endTime)) {
          const diff = endTime.diff(eTime);

          if (diff > -3600000 && beginTime.minutes() === 0) {
            beginTime.subtract(diff);
          }

          emit(beginTime.format("HH:mm:ss"));
        } else {
          emit(eTime.format("HH:mm:ss"));
        }
      }
    },
    allDay: {
      get() {
        return this.value?.allDay;
      },
      set(newValue) {
        this.$emit("input", {
          ...this.value,
          allDay: newValue,
          duration: newValue ? null : 30
        });
      }
    },
    sTimeLabel() {
      if (this.allDay) {
        return "All day";
      } else if (this.sTime) {
        return this.formatTimeOnly(this.sTime, "HH:mm");
      } else {
        return "";
      }
    },
    eTimeLabel() {
      if (this.allDay) {
        return "All day";
      } else if (this.eTime) {
        return this.formatTimeOnly(this.eTime, "HH:mm");
      } else {
        return "";
      }
    },
    variant() {
      return this.errors.length ? "outline-danger" : "text";
    }
  }
};
</script>
<style scoped>
.date-time-range-picker >>> .dropdown-toggle {
  padding: 0.375rem 0.75rem;
}
.date-time-range-picker >>> .b-form-btn-label-control.form-control {
  align-items: center;
}
</style>
